import { useState, useEffect, useRef } from 'react';
import { db, auth } from '../firebase/firebaseConfig';
import { collection, query, where, getDocs, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { calculateCourseCost, getCookbookCost } from '../config/contentPricingConfig';
import * as purchaseService from '../services/purchaseService';
import * as courseService from '../services/courseService';

/**
 * useCourseGenerator Hook
 * 
 * Manages all business logic and state for the CourseGenerator component.
 * Separates business logic from UI rendering.
 * 
 * @param {Object} props - Hook configuration
 * @param {Function} props.onGenerate - Callback for course generation
 * @param {Function} props.onGenerateCookbookOnly - Callback for cookbook generation
 * @param {number} props.tokenBalance - Initial token balance (from parent)
 * @param {Function} props.onTokenBalanceUpdate - Callback to update token balance in parent
 * @returns {Object} State and handlers for CourseGenerator component
 */
export const useCourseGenerator = ({
  onGenerate,
  onGenerateCookbookOnly,
  tokenBalance: propTokenBalance,
  onTokenBalanceUpdate
}) => {
  // Core state
  const [language, setLanguage] = useState('JavaScript');
  const [tokenBalance, setTokenBalance] = useState(propTokenBalance || 0);
  const [insufficientTokensError, setInsufficientTokensError] = useState('');
  const [availableLanguages, setAvailableLanguages] = useState([]);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [currentGenerationStep, setCurrentGenerationStep] = useState('');

  // View state
  const [currentView, setCurrentView] = useState('explore_grid');
  const [exploreInfoMode, setExploreInfoMode] = useState('static');
  const [exploreFilter, setExploreFilter] = useState('all');
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showLanguagesInGrid, setShowLanguagesInGrid] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showTypesInGrid, setShowTypesInGrid] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOwned, setFilterOwned] = useState(false);

  // Category state
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showCategoriesInGrid, setShowCategoriesInGrid] = useState(true); // Categories first on initial view
  const [availableCategories, setAvailableCategories] = useState([]);

  // Topic order state (loaded from CSV)
  const [topicOrderMap, setTopicOrderMap] = useState({});
  const [isLoadingTopicOrder, setIsLoadingTopicOrder] = useState(true);

  // Purchase history state
  const [showPurchaseHistory, setShowPurchaseHistory] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [purchaseFilter, setPurchaseFilter] = useState('all');
  const [purchaseTypeFilter, setPurchaseTypeFilter] = useState('all');
  const [isLoadingPurchases, setIsLoadingPurchases] = useState(false);
  const [restoringPurchaseId, setRestoringPurchaseId] = useState(null);

  // Content catalog state (for Python-generated content)
  const [catalogItems, setCatalogItems] = useState([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [newlyAddedItems, setNewlyAddedItems] = useState([]);
  const [isLoadingNewItems, setIsLoadingNewItems] = useState(false);

  // Refs
  const languageDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);
  const exploreSectionRef = useRef(null);
  const createCourseSectionRef = useRef(null);

  /**
   * Generate template courses based on selected language
   * DEPRECATED: No longer using hardcoded templates - using catalog items from Python pipeline instead
   */
  const getTemplateCourses = (selectedLanguage, category = 'fundamentals') => {
    // Return empty array - templates removed in favor of catalog items
    return [];
  };

  /**
   * Generate template cookbooks based on selected language
   * DEPRECATED: No longer using hardcoded templates - using catalog items from Python pipeline instead
   */
  const getTemplateCookbooks = (selectedLanguage, category = 'fundamentals') => {
    // Return empty array - templates removed in favor of catalog items
    return [];
  };

  /**
   * Load all available languages from the catalog
   * Only includes languages that have at least one published course or cookbook
   */
  const loadAvailableLanguages = async () => {
    try {
      const catalogRef = collection(db, 'content_catalog');
      const allItemsQuery = query(
        catalogRef,
        where('status', '==', 'published')
      );

      const snapshot = await getDocs(allItemsQuery);
      const languageSet = new Set();

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.language) {
          languageSet.add(data.language);
        }
      });

      // Convert to array and sort alphabetically
      const languages = Array.from(languageSet).sort();

      setAvailableLanguages(languages);
      console.log(`Loaded ${languages.length} available languages: ${languages.join(', ')}`);

      // If current language is not available, switch to first available language
      if (languages.length > 0 && !languages.includes(language)) {
        console.log(`Current language "${language}" not available, switching to "${languages[0]}"`);
        setLanguage(languages[0]);
      }
    } catch (error) {
      console.error('Error loading available languages:', error);
      // Fallback to default languages if query fails
      setAvailableLanguages(['JavaScript', 'Python']);
    }
  };

  /**
   * Load newly added catalog items (sorted by publishedAt or createdAt, newest first)
   * Shows items from all languages to highlight new content
   */
  const loadNewlyAddedItems = async () => {
    setIsLoadingNewItems(true);
    try {
      const catalogRef = collection(db, 'content_catalog');

      // Query for published items - always sort client-side to avoid index issues
      // Get more items than needed to ensure we have enough after sorting
      const newItemsQuery = query(
        catalogRef,
        where('status', '==', 'published'),
        firestoreLimit(200) // Get enough items to sort client-side
      );

      console.log('Loading newly added items from content_catalog...');
      const snapshot = await getDocs(newItemsQuery);
      console.log(`Found ${snapshot.docs.length} published items in catalog`);
      let items = snapshot.docs.map(doc => {
        const data = doc.data();

        // Get the best date for sorting (prefer publishedAt, fall back to createdAt)
        const publishedAt = data.publishedAt;
        const createdAt = data.createdAt;
        const sortDate = publishedAt || createdAt;

        return {
          id: doc.id,
          contentId: data.contentId,
          type: data.contentType,
          title: data.title,
          description: data.description,
          language: data.language,
          difficulty: data.difficulty,
          topic: data.topic,
          category: data.category || 'fundamentals',
          tokenCost: data.tokenCost,
          popularity: data.popularity || 0,
          rating: data.rating,
          reviewCount: data.reviewCount || 0,
          tags: data.tags || [],
          createdAt: createdAt,
          publishedAt: publishedAt,
          sortDate: sortDate, // For sorting
          isCatalogItem: true
        };
      });

      // Always sort client-side to ensure correct ordering
      // Use publishedAt if available (when item was actually published), otherwise createdAt
      items.sort((a, b) => {
        const getDate = (item) => {
          // Prefer publishedAt (when it was actually made available), fall back to createdAt
          const date = item.publishedAt || item.createdAt || item.sortDate;
          if (!date) {
            console.warn('Item missing date:', item.title, item.id);
            return new Date(0); // Very old date if missing
          }

          // Handle Firestore Timestamp
          if (date.toDate && typeof date.toDate === 'function') {
            return date.toDate();
          }
          // Handle timestamp object with seconds
          if (date.seconds !== undefined) {
            return new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000);
          }
          // Handle Date object
          if (date instanceof Date) {
            return date;
          }
          // Handle string or number
          const parsed = new Date(date);
          if (isNaN(parsed.getTime())) {
            console.warn('Invalid date for item:', item.title, date);
            return new Date(0);
          }
          return parsed;
        };

        const dateA = getDate(a);
        const dateB = getDate(b);

        return dateB.getTime() - dateA.getTime(); // Newest first
      });

      // Take top 12 newest items
      const newestItems = items.slice(0, 12);

      console.log(`[Newly Added] Loaded ${newestItems.length} newly added items (out of ${items.length} total published items)`);
      if (newestItems.length > 0) {
        console.log('[Newly Added] Items:', newestItems.map(i => ({
          title: i.title,
          language: i.language,
          type: i.type,
          status: 'published',
          publishedAt: i.publishedAt ? (i.publishedAt.toDate ? i.publishedAt.toDate().toISOString() : i.publishedAt) : 'N/A',
          createdAt: i.createdAt ? (i.createdAt.toDate ? i.createdAt.toDate().toISOString() : i.createdAt) : 'N/A'
        })));
      } else if (items.length === 0) {
        console.warn('[Newly Added] No published items found in catalog. Check if items have status="published"');
      } else {
        console.log(`[Newly Added] Found ${items.length} items but none in top 12. Showing first 12 items.`);
        // If we have items but they're not sorted properly, show the first 12 anyway
        setNewlyAddedItems(items.slice(0, 12));
        return;
      }

      setNewlyAddedItems(newestItems);
    } catch (error) {
      console.error('[Newly Added] Error loading newly added items:', error);
      console.error('[Newly Added] Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });

      // Try a simpler query as fallback
      try {
        console.log('[Newly Added] Attempting fallback query without status filter...');
        const fallbackQuery = query(
          catalogRef,
          firestoreLimit(50)
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        const fallbackItems = fallbackSnapshot.docs
          .map(doc => {
            const data = doc.data();
            if (data.status !== 'published') return null; // Filter client-side
            return {
              id: doc.id,
              contentId: data.contentId,
              type: data.contentType,
              title: data.title,
              description: data.description,
              language: data.language,
              difficulty: data.difficulty,
              topic: data.topic,
              category: data.category || 'fundamentals',
              tokenCost: data.tokenCost,
              popularity: data.popularity || 0,
              rating: data.rating,
              reviewCount: data.reviewCount || 0,
              tags: data.tags || [],
              createdAt: data.createdAt,
              publishedAt: data.publishedAt,
              sortDate: data.publishedAt || data.createdAt,
              isCatalogItem: true
            };
          })
          .filter(item => item !== null);

        // Sort by date
        fallbackItems.sort((a, b) => {
          const getDate = (item) => {
            const date = item.publishedAt || item.createdAt;
            if (!date) return new Date(0);
            if (date.toDate) return date.toDate();
            if (date.seconds !== undefined) return new Date(date.seconds * 1000);
            return new Date(date);
          };
          return getDate(b).getTime() - getDate(a).getTime();
        });

        console.log(`[Newly Added] Fallback query found ${fallbackItems.length} published items`);
        setNewlyAddedItems(fallbackItems.slice(0, 12));
      } catch (fallbackError) {
        console.error('[Newly Added] Fallback query also failed:', fallbackError);
        setNewlyAddedItems([]);
      }
    } finally {
      setIsLoadingNewItems(false);
    }
  };

  /**
   * Fetch content catalog items (Python-generated content)
   */
  const loadContentCatalog = async () => {
    setIsLoadingCatalog(true);
    try {
      const catalogRef = collection(db, 'content_catalog');

      // Build query - try with orderBy first, fallback if index missing
      let languageQuery;
      let useOrderBy = true;
      try {
        languageQuery = query(
          catalogRef,
          where('status', '==', 'published'),
          where('language', '==', language),
          orderBy('popularity', 'desc'),
          firestoreLimit(50) // Limit to 50 items per language
        );
      } catch (orderByError) {
        // If orderBy fails during query construction, use simple query
        console.log('OrderBy not available, using simple query');
        useOrderBy = false;
        languageQuery = query(
          catalogRef,
          where('status', '==', 'published'),
          where('language', '==', language),
          firestoreLimit(50)
        );
      }

      // If category is specified and not 'all', add category filter
      // Note: This requires a composite index in Firestore
      // For now, we'll filter client-side if category field exists
      const snapshot = await getDocs(languageQuery);
      let items = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          contentId: data.contentId, // Reference to courses/{courseId} or cookbooks/{cookbookId}
          type: data.contentType, // 'course' or 'cookbook'
          title: data.title,
          description: data.description,
          language: data.language,
          difficulty: data.difficulty,
          topic: data.topic,
          category: data.category || 'fundamentals', // Default to 'fundamentals' if not set
          tokenCost: data.tokenCost,
          popularity: data.popularity || 0,
          rating: data.rating,
          reviewCount: data.reviewCount || 0,
          tags: data.tags || [],
          createdAt: data.createdAt,
          isCatalogItem: true // Mark as catalog item (not template)
        };
      });

      // Filter by category client-side (until composite index is set up)
      if (selectedCategory !== 'all') {
        const beforeFilter = items.length;
        items = items.filter(item => item.category === selectedCategory);
        console.log(`After category filter ('${selectedCategory}'): ${beforeFilter} -> ${items.length} items`);
      }

      // If we didn't use orderBy, sort client-side
      if (!useOrderBy) {
        items.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
      }

      console.log(`Final catalog items count: ${items.length}`);
      if (items.length > 0) {
        console.log('Sample items:', items.slice(0, 3).map(i => ({ id: i.id, title: i.title, language: i.language, status: 'published' })));
      }

      setCatalogItems(items);

      // Extract available categories from all items (before category filter)
      // We need to get all items for the language to determine available categories
      const allItemsForLanguage = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          category: data.category || 'fundamentals'
        };
      });
      const categories = extractAvailableCategories(allItemsForLanguage);
      setAvailableCategories(categories);

      // If selected category is not available for this language, reset to 'all'
      if (selectedCategory !== 'all' && !categories.find(c => c.value === selectedCategory)) {
        console.log(`Category "${selectedCategory}" not available for language "${language}", resetting to "all"`);
        setSelectedCategory('all');
      }

      console.log(`Loaded ${items.length} catalog items for language "${language}", category "${selectedCategory}"`);
      console.log(`Available categories: ${categories.map(c => c.value).join(', ')}`);
      if (items.length === 0) {
        console.log('No catalog items found. Check: 1) Language filter matches catalog entries, 2) Status is "published", 3) Firestore index exists');
      }
    } catch (error) {
      console.error('Error loading content catalog:', error);
      console.error('Error details:', error.code, error.message);
      // If it's an index error, try querying without orderBy
      if (error.code === 'failed-precondition') {
        console.log('Firestore index missing. Attempting query without orderBy...');
        try {
          const simpleQuery = query(
            catalogRef,
            where('status', '==', 'published'),
            where('language', '==', language)
          );
          const simpleSnapshot = await getDocs(simpleQuery);
          let simpleItems = simpleSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              contentId: data.contentId,
              type: data.contentType,
              title: data.title,
              description: data.description,
              language: data.language,
              difficulty: data.difficulty,
              topic: data.topic,
              category: data.category || 'fundamentals',
              tokenCost: data.tokenCost,
              popularity: data.popularity || 0,
              rating: data.rating,
              reviewCount: data.reviewCount || 0,
              tags: data.tags || [],
              createdAt: data.createdAt,
              isCatalogItem: true
            };
          });

          // Filter by category client-side
          if (selectedCategory !== 'all') {
            simpleItems = simpleItems.filter(item => item.category === selectedCategory);
          }

          // Sort by popularity client-side
          simpleItems.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));

          setCatalogItems(simpleItems);

          // Extract available categories from all items for the language
          const allItemsForLanguage = simpleSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              category: data.category || 'fundamentals'
            };
          });
          const categories = extractAvailableCategories(allItemsForLanguage);
          setAvailableCategories(categories);

          // If selected category is not available for this language, reset to 'all'
          if (selectedCategory !== 'all' && !categories.find(c => c.value === selectedCategory)) {
            console.log(`Category "${selectedCategory}" not available for language "${language}", resetting to "all"`);
            setSelectedCategory('all');
          }

          console.log(`Loaded ${simpleItems.length} catalog items (without orderBy)`);
          console.log(`Available categories: ${categories.map(c => c.value).join(', ')}`);
        } catch (simpleError) {
          console.error('Simple query also failed:', simpleError);
          setCatalogItems([]);
        }
      } else {
        setCatalogItems([]);
      }
    } finally {
      setIsLoadingCatalog(false);
    }
  };

  // Get templates filtered by category (deprecated - returns empty arrays)
  const templateCourses = getTemplateCourses(language, selectedCategory);
  const templateCookbooks = getTemplateCookbooks(language, selectedCategory);

  // Use only catalog items (templates removed)
  const allAvailableItems = [...catalogItems];

  // Filter based on exploreFilter
  let filteredTemplates = exploreFilter === 'courses'
    ? allAvailableItems.filter(item => item.type === 'course')
    : exploreFilter === 'cookbooks'
      ? allAvailableItems.filter(item => item.type === 'cookbook')
      : allAvailableItems; // 'all' case

  // Apply search query filter
  if (searchQuery.trim()) {
    const query = searchQuery.trim().toLowerCase();
    filteredTemplates = filteredTemplates.filter(item => {
      const titleMatch = item.title?.toLowerCase().includes(query);
      const descriptionMatch = item.description?.toLowerCase().includes(query);
      const languageMatch = item.language?.toLowerCase().includes(query);
      const categoryMatch = item.category?.toLowerCase().includes(query);
      const tagsMatch = item.tags?.some(tag => tag.toLowerCase().includes(query));
      return titleMatch || descriptionMatch || languageMatch || categoryMatch || tagsMatch;
    });
  }

  // Apply owned filter
  if (filterOwned) {
    filteredTemplates = filteredTemplates.filter(item => {
      if (!item || !item.isCatalogItem || !item.contentId) {
        return false;
      }
      // Check if there's a purchase with matching contentId and contentType
      return purchases.some(purchase => {
        const contentTypeMatches = purchase.contentType === item.type;
        const contentIdMatches = purchase.contentId === item.contentId ||
          purchase.metadata?.catalogId === item.contentId ||
          purchase.metadata?.contentId === item.contentId ||
          purchase.originalId === item.contentId;
        return contentTypeMatches && contentIdMatches && purchase.status === 'active';
      });
    });
  }

  /**
   * Load topic order from CSV file
   * Parses the CSV and creates a mapping of topic -> learning sequence order
   */
  const loadTopicOrder = async () => {
    try {
      setIsLoadingTopicOrder(true);

      // Fetch the CSV file from the public folder
      // It's served as a static asset at /topic_order.csv
      const csvPath = process.env.PUBLIC_URL
        ? `${process.env.PUBLIC_URL}/topic_order.csv`
        : '/topic_order.csv';

      const response = await fetch(csvPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch topic_order.csv: ${response.status} ${response.statusText}`);
      }
      const csvText = await response.text();

      // Parse CSV
      const lines = csvText.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      // Find column indices
      const topicIndex = headers.indexOf('topic');
      const sequenceIndex = headers.indexOf('learning_sequence');

      if (topicIndex === -1 || sequenceIndex === -1) {
        throw new Error('CSV missing required columns: topic or learning_sequence');
      }

      // Build mapping from CSV data
      const orderMap = {};
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const values = line.split(',').map(v => v.trim());
        const topic = values[topicIndex];
        const sequence = parseInt(values[sequenceIndex], 10);

        if (topic && !isNaN(sequence)) {
          // Normalize topic name (handle variations)
          const topicLower = topic.toLowerCase().replace(/[_-]/g, '_');
          orderMap[topicLower] = sequence;

          // Also add variations (with hyphens, without separators, etc.)
          orderMap[topic.toLowerCase().replace(/[_-]/g, '-')] = sequence;
          orderMap[topic.toLowerCase().replace(/[_-]/g, '')] = sequence;
          orderMap[topic.toLowerCase()] = sequence;
        }
      }

      setTopicOrderMap(orderMap);
      console.log('Topic order loaded from CSV:', orderMap);
    } catch (error) {
      console.error('Error loading topic order CSV:', error);
      // Fallback to empty map (items will be sorted by popularity/title only)
      setTopicOrderMap({});
    } finally {
      setIsLoadingTopicOrder(false);
    }
  };

  /**
   * Get topic order from dynamically loaded CSV mapping
   * Lower numbers = earlier in learning sequence
   */
  const getTopicOrder = (topic) => {
    if (!topic || !topicOrderMap || Object.keys(topicOrderMap).length === 0) {
      return 999; // Items without topic or if CSV not loaded go to end
    }

    const topicLower = topic.toLowerCase().replace(/[_-]/g, '_');

    // Check exact match first
    if (topicOrderMap[topicLower] !== undefined) {
      return topicOrderMap[topicLower];
    }

    // Check variations (with hyphens, without separators)
    const topicVariations = [
      topic.toLowerCase().replace(/[_-]/g, '-'),
      topic.toLowerCase().replace(/[_-]/g, ''),
      topic.toLowerCase()
    ];

    for (const variation of topicVariations) {
      if (topicOrderMap[variation] !== undefined) {
        return topicOrderMap[variation];
      }
    }

    // Check partial match (if topic contains any key from the map)
    for (const [key, order] of Object.entries(topicOrderMap)) {
      if (topicLower.includes(key) || key.includes(topicLower)) {
        return order;
      }
    }

    return 999; // Unknown topics go to end
  };

  // Sort by topic order (learning sequence), then by popularity for same topic
  filteredTemplates.sort((a, b) => {
    const orderA = getTopicOrder(a.topic);
    const orderB = getTopicOrder(b.topic);

    // First sort by topic order (learning sequence)
    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // If same topic order, sort by popularity (descending)
    const popularityA = a.popularity || 0;
    const popularityB = b.popularity || 0;
    if (popularityA !== popularityB) {
      return popularityB - popularityA;
    }

    // If same popularity, sort alphabetically by title
    return (a.title || '').localeCompare(b.title || '');
  });

  /**
   * Calculate cost for a generation type
   */
  const calculateCost = (generationType) => {
    return generationType === 'course'
      ? calculateCourseCost('beginner')
      : getCookbookCost(false);
  };

  /**
   * Check if user has sufficient tokens
   */
  const hasSufficientTokens = (cost) => {
    return tokenBalance >= cost;
  };

  /**
   * Start progress simulation for generation
   */
  const startProgressSimulation = () => {
    setIsGenerating(true);
    setGenerationProgress(0);
    setCurrentGenerationStep('analyzing');

    const progressInterval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 10;
      });
    }, 500);

    const stepInterval = setInterval(() => {
      setCurrentGenerationStep(prev => {
        const steps = ['analyzing', 'structuring', 'generating', 'finalizing'];
        const currentIndex = steps.indexOf(prev);
        if (currentIndex < steps.length - 1) {
          return steps[currentIndex + 1];
        }
        return prev;
      });
    }, 2000);

    return { progressInterval, stepInterval };
  };

  /**
   * Complete progress simulation
   */
  const completeProgressSimulation = async (intervals) => {
    setGenerationProgress(100);
    setCurrentGenerationStep('finalizing');
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (intervals) {
      clearInterval(intervals.progressInterval);
      clearInterval(intervals.stepInterval);
    }

    setIsGenerating(false);
    setGenerationProgress(0);
    setCurrentGenerationStep('');
  };

  /**
   * Update token balance after purchase
   */
  const updateTokenBalanceAfterPurchase = async (cost) => {
    const newBalance = tokenBalance - cost;
    setTokenBalance(newBalance);
    if (onTokenBalanceUpdate) {
      onTokenBalanceUpdate(newBalance);
    }
  };

  /**
   * Handle template purchase (generate new) or catalog item purchase (purchase existing)
   */
  const handleBuyTemplate = async () => {
    if (!selectedTemplate) return;

    // All items should be catalog items now (templates removed)
    // Purchase existing content from catalog
    if (selectedTemplate.isCatalogItem) {
      await handlePurchaseCatalogItem();
      return;
    }

    // If somehow we get a non-catalog item, show error
    alert('This content is no longer available. Please select from the catalog.');
    setCurrentView('explore_grid');
    setSelectedTemplate(null);
  };

  /**
   * Handle purchasing existing content from catalog
   * This will create a purchase record and copy content from shared collections to user's collections
   */
  const handlePurchaseCatalogItem = async () => {
    if (!selectedTemplate || !selectedTemplate.isCatalogItem) return;

    const cost = selectedTemplate.tokenCost || (selectedTemplate.type === 'cookbook' ? 5 : 15);

    if (!hasSufficientTokens(cost)) {
      setInsufficientTokensError(
        `You need ${cost} tokens to purchase this ${selectedTemplate.type === 'cookbook' ? 'cookbook' : 'course'}. You have ${tokenBalance} tokens.`
      );
      return;
    }

    setInsufficientTokensError('');
    const intervals = startProgressSimulation();
    setCurrentGenerationStep('purchasing');

    try {
      // Purchase from catalog using catalog ID (which is the same as contentId in catalog entries)
      const result = await purchaseService.purchaseCatalogContent(selectedTemplate.id);

      await completeProgressSimulation(intervals);

      // Update token balance from result
      if (result.tokenBalance !== undefined) {
        setTokenBalance(result.tokenBalance);
        if (onTokenBalanceUpdate) {
          onTokenBalanceUpdate(result.tokenBalance);
        }
      } else {
        await updateTokenBalanceAfterPurchase(cost);
      }

      // Refresh catalog (update popularity)
      await loadContentCatalog();

      // Refresh purchases so the item appears in "Owned" section
      await loadPurchaseHistory();

      alert(result.message || `${selectedTemplate.type === 'cookbook' ? 'Cookbook' : 'Course'} purchased successfully!`);
      setCurrentView('explore_grid');
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Failed to purchase catalog item:', error);
      const errorMessage = error.message || error.details?.message || 'Unknown error occurred';
      if (error.code === 'failed-precondition' || errorMessage.includes('Insufficient tokens')) {
        setInsufficientTokensError(errorMessage);
      } else {
        alert(`Failed to purchase ${selectedTemplate.type === 'cookbook' ? 'cookbook' : 'course'}: ${errorMessage}`);
      }
      completeProgressSimulation(intervals);
    }
  };

  /**
   * Handle form-based generation
   * DEPRECATED: Custom generation removed - users should purchase from catalog instead
   */
  const handleGenerateFromForm = async (formData) => {
    alert('Custom generation is no longer available. Please browse and purchase from the catalog.');
    setCurrentView('explore_grid');
    setSelectedTemplate(null);
  };

  /**
   * Load purchase history
   */
  const loadPurchaseHistory = async () => {
    setIsLoadingPurchases(true);
    try {
      const purchases = await purchaseService.getPurchaseHistory({
        status: purchaseFilter !== 'all' ? purchaseFilter : undefined,
        contentType: purchaseTypeFilter !== 'all' ? purchaseTypeFilter : undefined
      });
      setPurchases(purchases);
    } catch (error) {
      console.error('Error loading purchase history:', error);
      setPurchases([]);
    } finally {
      setIsLoadingPurchases(false);
    }
  };

  /**
   * Restore a purchase
   */
  const handleRestorePurchase = async (purchaseId, purchaseTitle) => {
    if (!window.confirm(`Restore "${purchaseTitle}"? It will be added back to your ${purchases.find(p => p.id === purchaseId)?.contentType === 'course' ? 'courses' : 'cookbooks'} list.`)) {
      return;
    }

    setRestoringPurchaseId(purchaseId);
    try {
      const result = await purchaseService.restorePurchase(purchaseId);
      alert(result.message || 'Purchase restored successfully!');
      await loadPurchaseHistory();

      // Refresh token balance
      if (onTokenBalanceUpdate) {
        const newBalance = await purchaseService.getTokenBalance();
        onTokenBalanceUpdate(newBalance);
      }
    } catch (error) {
      console.error('Error restoring purchase:', error);
      alert(`Failed to restore purchase: ${error.message || 'Unknown error'}`);
    } finally {
      setRestoringPurchaseId(null);
    }
  };

  /**
   * Get display name for a category
   * Maps category values to user-friendly labels
   */
  const getCategoryDisplayName = (category) => {
    const categoryMap = {
      'fundamentals': 'Fundamentals',
      'advanced': 'Advanced',
      'web-development': 'Web Development',
      'data-science': 'Data Science'
    };
    // If category is in map, use mapped value, otherwise capitalize words
    if (categoryMap[category]) {
      return categoryMap[category];
    }
    // Fallback: convert kebab-case to Title Case
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  /**
   * Extract available categories from catalog items
   * Only includes categories that have at least one course or cookbook
   * Also counts items per category
   */
  const extractAvailableCategories = (items) => {
    const categoryCounts = {};
    items.forEach(item => {
      if (item.category) {
        categoryCounts[item.category] = (categoryCounts[item.category] || 0) + 1;
      }
    });

    // Convert to array and sort for consistent display
    const categories = Object.keys(categoryCounts).sort();

    // Create category objects with value, label, and count
    return categories.map(cat => ({
      value: cat,
      label: getCategoryDisplayName(cat),
      count: categoryCounts[cat]
    }));
  };

  /**
   * Format date for display
   */
  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return 'Unknown';

      let date;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        date = new Date(timestamp);
      }

      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Unknown';
    }
  };

  /**
   * Check if content exists in user's database
   * @param {string} contentId - The content ID to check
   * @param {string} contentType - 'course' or 'cookbook'
   * @returns {Promise<boolean>} - True if content exists in user's DB
   */
  const checkContentExists = async (contentId, contentType) => {
    if (!auth.currentUser || !contentId) {
      return false;
    }

    try {
      const userId = auth.currentUser.uid;

      if (contentType === 'course') {
        // Check in user's courses collection where originalSharedId matches
        const coursesRef = collection(db, `users/${userId}/courses`);
        const coursesQuery = query(coursesRef, where('originalSharedId', '==', contentId));
        const coursesSnapshot = await getDocs(coursesQuery);
        return !coursesSnapshot.empty;
      } else if (contentType === 'cookbook') {
        // Check in user's decks collection where originalSharedId matches
        const decksRef = collection(db, `users/${userId}/decks`);
        const decksQuery = query(decksRef, where('originalSharedId', '==', contentId));
        const decksSnapshot = await getDocs(decksQuery);
        return !decksSnapshot.empty;
      }

      return false;
    } catch (error) {
      console.error('Error checking if content exists:', error);
      return false;
    }
  };

  /**
   * Get ownership and existence status for a template
   * @param {Object} template - The template/catalog item to check
   * @returns {Promise<Object>} - Object with { isOwned, exists, purchaseId }
   */
  const getTemplateOwnershipStatus = async (template) => {
    if (!template || !template.isCatalogItem || !template.contentId) {
      return { isOwned: false, exists: false, purchaseId: null };
    }

    // Check if there's a purchase with matching contentId and contentType
    const purchase = purchases.find(p => {
      const contentTypeMatches = p.contentType === template.type;
      const contentIdMatches = p.contentId === template.contentId ||
        p.metadata?.catalogId === template.contentId ||
        p.metadata?.contentId === template.contentId ||
        p.originalId === template.contentId;
      return contentTypeMatches && contentIdMatches && p.status === 'active';
    });

    if (!purchase) {
      return { isOwned: false, exists: false, purchaseId: null };
    }

    // Check if content exists in user's database
    const exists = await checkContentExists(template.contentId, template.type);

    return {
      isOwned: true,
      exists,
      purchaseId: purchase.id
    };
  };

  /**
   * Check if a template/catalog item is already owned (purchased)
   * @param {Object} template - The template/catalog item to check
   * @returns {boolean} - True if the item is owned
   */
  const isTemplateOwned = (template) => {
    if (!template || !template.isCatalogItem || !template.contentId) {
      return false;
    }

    // Check if there's a purchase with matching contentId and contentType
    return purchases.some(purchase => {
      const contentTypeMatches = purchase.contentType === template.type;
      const contentIdMatches = purchase.contentId === template.contentId ||
        purchase.metadata?.catalogId === template.contentId ||
        purchase.metadata?.contentId === template.contentId ||
        purchase.originalId === template.contentId;
      return contentTypeMatches && contentIdMatches && purchase.status === 'active';
    });
  };

  // View handlers
  const handleExampleClick = (template) => {
    setSelectedTemplate(template);
    setExploreInfoMode('static');
    setCurrentView('explore_preview');
  };

  const handleViewDetails = (template) => {
    setSelectedTemplate(template);
    setExploreInfoMode('static');
    setCurrentView('explore_info');
  };

  const handleCardClick = () => {
    if (filteredTemplates.length > 0 && filteredTemplates[currentCardIndex]) {
      handleExampleClick(filteredTemplates[currentCardIndex]);
    }
  };

  const handleNextCard = () => {
    if (filteredTemplates.length > 0) {
      setCurrentCardIndex((prev) => (prev + 1) % filteredTemplates.length);
    }
  };

  const handlePreviousCard = () => {
    if (filteredTemplates.length > 0) {
      setCurrentCardIndex((prev) => (prev - 1 + filteredTemplates.length) % filteredTemplates.length);
    }
  };

  const handleShuffle = () => {
    if (filteredTemplates.length > 0) {
      const randomIndex = Math.floor(Math.random() * filteredTemplates.length);
      setCurrentCardIndex(randomIndex);
    }
  };

  const handleBackToFront = () => {
    setCurrentView('explore_grid');
    setSelectedTemplate(null);
  };

  /**
   * Handle create course button click
   * DEPRECATED: Custom course creation removed - users should purchase from catalog instead
   */
  /**
   * Handle create course button click
   * DEPRECATED: Custom course creation removed - users should purchase from catalog instead
   */
  const handleCreateCourse = () => {
    alert('Custom course creation is no longer available. Please browse and purchase from the catalog.');
    // Scroll to explore section to show catalog items
    scrollToExplore();
  };

  const handleLanguageSelect = (selectedLanguage) => {
    setLanguage(selectedLanguage);
    setShowLanguageDropdown(false);
    setShowLanguagesInGrid(false);
  };

  const handleTypeSelect = (type) => {
    setExploreFilter(type);
    setShowTypeDropdown(false);
    setShowTypesInGrid(false);
    setCurrentCardIndex(0);
  };

  const handleCategorySelect = (category) => {
    // Only allow selecting 'all' or categories that exist in availableCategories
    const isValidCategory = category === 'all' || availableCategories.find(c => c.value === category);
    if (isValidCategory) {
      setSelectedCategory(category);
      setShowCategoryDropdown(false);
      setShowCategoriesInGrid(false); // Close category grid view when category is selected
      // Reset card index when category changes
      setCurrentCardIndex(0);
      // Reset scroll position to left when category changes
      // This will be handled by the component's exploreGridRef
    } else {
      console.warn(`Attempted to select invalid category: ${category}`);
    }
  };

  const scrollToExplore = () => {
    setShowPurchaseHistory(false);
    setTimeout(() => {
      if (exploreSectionRef.current) {
        exploreSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const scrollToCreateCourse = () => {
    if (createCourseSectionRef.current) {
      createCourseSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Fetch token balance on mount or when prop changes
  useEffect(() => {
    const fetchTokenBalance = async () => {
      if (propTokenBalance !== undefined) {
        setTokenBalance(propTokenBalance);
        return;
      }

      try {
        const balance = await purchaseService.getTokenBalance();
        setTokenBalance(balance);
      } catch (error) {
        console.error('Error fetching token balance:', error);
        setTokenBalance(0);
      }
    };

    fetchTokenBalance();
  }, [propTokenBalance]);

  // Update token balance when prop changes
  useEffect(() => {
    if (propTokenBalance !== undefined) {
      setTokenBalance(propTokenBalance);
    }
  }, [propTokenBalance]);

  // Reset card index when filter changes
  useEffect(() => {
    setCurrentCardIndex(0);
  }, [exploreFilter]);

  // Load purchases when purchase history is shown or filters change
  // Also load purchases on mount to check ownership status
  useEffect(() => {
    if (showPurchaseHistory) {
      loadPurchaseHistory();
    } else {
      // Load purchases even when not showing history, to check ownership
      loadPurchaseHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPurchaseHistory, purchaseFilter, purchaseTypeFilter]);

  // Load content catalog when language or category changes
  useEffect(() => {
    loadContentCatalog();
    // Reset scroll position when category changes (will be handled by component)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, selectedCategory]);

  // Load topic order from CSV on mount
  useEffect(() => {
    loadTopicOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load available languages on mount
  useEffect(() => {
    loadAvailableLanguages();
    // Refresh available languages periodically (every 10 minutes)
    const interval = setInterval(() => {
      loadAvailableLanguages();
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load newly added items on mount and periodically
  useEffect(() => {
    loadNewlyAddedItems();
    // Refresh newly added items every 5 minutes
    const interval = setInterval(() => {
      loadNewlyAddedItems();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (currentView === 'explore_preview') {
          setCurrentView('explore_info');
        } else if (currentView === 'explore_info') {
          setCurrentView('explore_grid');
          setSelectedTemplate(null);
        }
      }
    };

    if (currentView === 'explore_info' || currentView === 'explore_preview') {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [currentView]);

  return {
    // State
    language,
    setLanguage,
    availableLanguages,
    tokenBalance,
    insufficientTokensError,
    isGenerating,
    generationProgress,
    currentGenerationStep,
    currentView,
    setCurrentView,
    exploreInfoMode,
    setExploreInfoMode,
    exploreFilter,
    setExploreFilter,
    searchQuery,
    setSearchQuery,
    filterOwned,
    setFilterOwned,
    currentCardIndex,
    selectedTemplate,
    showLanguageDropdown,
    setShowLanguageDropdown,
    showLanguagesInGrid,
    setShowLanguagesInGrid,
    showTypeDropdown,
    setShowTypeDropdown,
    showTypesInGrid,
    setShowTypesInGrid,
    showPurchaseHistory,
    setShowPurchaseHistory,
    purchases,
    purchaseFilter,
    setPurchaseFilter,
    purchaseTypeFilter,
    setPurchaseTypeFilter,
    isLoadingPurchases,
    restoringPurchaseId,

    // Refs
    languageDropdownRef,
    categoryDropdownRef,
    typeDropdownRef,
    exploreSectionRef,
    createCourseSectionRef,

    // Templates and Catalog
    templateCourses,
    templateCookbooks,
    catalogItems,
    isLoadingCatalog,
    filteredTemplates,
    newlyAddedItems,
    isLoadingNewItems,

    // Category
    selectedCategory,
    setSelectedCategory,
    showCategoryDropdown,
    setShowCategoryDropdown,
    showCategoriesInGrid,
    setShowCategoriesInGrid,
    handleCategorySelect,
    availableCategories,
    getCategoryDisplayName,

    // Handlers
    handleExampleClick,
    handleViewDetails,
    handleCardClick,
    handleNextCard,
    handlePreviousCard,
    handleShuffle,
    handleBackToFront,
    handleCreateCourse,
    handleBuyTemplate,
    handleGenerateFromForm,
    handleLanguageSelect,
    handleTypeSelect,
    handleRestorePurchase,
    scrollToExplore,
    scrollToCreateCourse,
    formatDate,
    calculateCost,
    hasSufficientTokens,
    isTemplateOwned,
    getTemplateOwnershipStatus,
    checkContentExists
  };
};

